import { useState, useEffect } from "react";
import { getDashboard, getDashboardWeekly } from "@/lib/api";
import { formatNumber, formatDateTime } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowDownToLine, 
  ArrowUpFromLine, 
  Package, 
  Activity,
  Fish,
  ChevronLeft,
  ChevronRight,
  Calendar
} from "lucide-react";
import { motion } from "framer-motion";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [giacenzaAttuale, setGiacenzaAttuale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);

  useEffect(() => {
    // Initialize to current week's Monday
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
    
    // Load current giacenza (fixed, doesn't change with weeks)
    loadGiacenzaAttuale();
  }, []);

  useEffect(() => {
    if (currentWeekStart) {
      loadWeeklyData();
    }
  }, [currentWeekStart]);

  const loadGiacenzaAttuale = async () => {
    try {
      // Get current week data which includes current giacenza
      const today = new Date();
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      const weekStartStr = monday.toISOString().split('T')[0];
      
      const [dashRes, weeklyRes] = await Promise.all([
        getDashboard(),
        getDashboardWeekly(weekStartStr)
      ]);
      
      setStats(dashRes.data);
      // Store current giacenza separately
      setGiacenzaAttuale(weeklyRes.data?.per_prodotto || []);
    } catch (error) {
      console.error("Error loading giacenza:", error);
    }
  };

  const loadWeeklyData = async () => {
    setLoading(true);
    try {
      const weekStartStr = currentWeekStart.toISOString().split('T')[0];
      const weeklyRes = await getDashboardWeekly(weekStartStr);
      setWeeklyStats(weeklyRes.data);
    } catch (error) {
      console.error("Error loading weekly data:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => {
    const prevWeek = new Date(currentWeekStart);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentWeekStart(prevWeek);
  };

  const goToNextWeek = () => {
    const nextWeek = new Date(currentWeekStart);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentWeekStart(nextWeek);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  const formatWeekRange = () => {
    if (!currentWeekStart) return "";
    const sunday = new Date(currentWeekStart);
    sunday.setDate(sunday.getDate() + 6);
    
    const options = { day: '2-digit', month: 'short' };
    const mondayStr = currentWeekStart.toLocaleDateString('it-IT', options);
    const sundayStr = sunday.toLocaleDateString('it-IT', options);
    const year = currentWeekStart.getFullYear();
    
    return `${mondayStr} - ${sundayStr} ${year}`;
  };

  const isCurrentWeek = () => {
    if (!currentWeekStart) return true;
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const thisMonday = new Date(today);
    thisMonday.setDate(today.getDate() + diff);
    thisMonday.setHours(0, 0, 0, 0);
    return currentWeekStart.getTime() === thisMonday.getTime();
  };

  if (loading && !weeklyStats && !giacenzaAttuale.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const perProdottoSettimanale = weeklyStats?.per_prodotto || [];

  // Colori per i prodotti
  const productColors = [
    { bg: "from-cyan-500 to-cyan-600", text: "text-white", light: "from-cyan-50 to-cyan-100", lightText: "text-cyan-700", border: "border-cyan-200" },
    { bg: "from-violet-500 to-violet-600", text: "text-white", light: "from-violet-50 to-violet-100", lightText: "text-violet-700", border: "border-violet-200" },
    { bg: "from-rose-500 to-rose-600", text: "text-white", light: "from-rose-50 to-rose-100", lightText: "text-rose-700", border: "border-rose-200" },
    { bg: "from-teal-500 to-teal-600", text: "text-white", light: "from-teal-50 to-teal-100", lightText: "text-teal-700", border: "border-teal-200" },
    { bg: "from-amber-500 to-amber-600", text: "text-white", light: "from-amber-50 to-amber-100", lightText: "text-amber-700", border: "border-amber-200" },
  ];

  return (
    <div data-testid="dashboard-page" className="w-full space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Panoramica giacenza e riepilogo settimanale
        </p>
      </div>

      {/* FIXED SECTION: Partite Attive + Giacenza Attuale per Prodotto */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Partite Attive */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-0 shadow-lg h-full">
            <CardContent className="p-4 flex flex-col justify-between h-full">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-400" />
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Partite Attive</p>
              </div>
              <p className="font-heading text-4xl font-bold text-white mt-2">{weeklyStats?.partite_attive || stats?.partite_attive || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Giacenza per Prodotto (FIXED - non cambia con le settimane) */}
        {giacenzaAttuale.map((prod, index) => {
          const colors = productColors[index % productColors.length];
          return (
            <motion.div 
              key={prod.prodotto} 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * (index + 1) }}
            >
              <Card className={`bg-gradient-to-br ${colors.bg} border-0 shadow-lg h-full`}>
                <CardContent className="p-4 flex flex-col justify-between h-full">
                  <div className="flex items-center gap-2">
                    <Fish className="w-5 h-5 text-white/70" />
                    <p className={`text-xs font-medium ${colors.text} opacity-80 uppercase tracking-wide`}>{prod.prodotto}</p>
                  </div>
                  <div>
                    <p className={`font-heading text-3xl font-bold ${colors.text}`}>
                      {formatNumber(prod.disponibile_fine_settimana || 0)}
                    </p>
                    <p className={`text-sm ${colors.text} opacity-70`}>kg disponibili</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* WEEKLY SECTION */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="font-heading text-base lg:text-lg font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Riepilogo Settimanale
            </CardTitle>
            
            {/* Week Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousWeek}
                className="h-8 px-2"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg min-w-[180px] justify-center">
                <span className="font-medium text-slate-700 text-sm">
                  {formatWeekRange()}
                </span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextWeek}
                disabled={isCurrentWeek()}
                className="h-8 px-2"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              {!isCurrentWeek() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToCurrentWeek}
                  className="h-8 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  Oggi
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : perProdottoSettimanale.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {perProdottoSettimanale.map((prod, index) => {
                const colors = productColors[index % productColors.length];
                const saldo = prod.entrate - prod.uscite - prod.scarto;
                
                return (
                  <Card 
                    key={prod.prodotto} 
                    className={`bg-gradient-to-br ${colors.light} ${colors.border} border shadow-sm`}
                  >
                    <CardContent className="p-4">
                      {/* Product Header */}
                      <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/50">
                        <div className="flex items-center gap-2">
                          <Fish className={`w-4 h-4 ${colors.lightText}`} />
                          <h3 className={`font-heading font-bold ${colors.lightText}`}>
                            {prod.prodotto}
                          </h3>
                        </div>
                        <span className="text-xs text-slate-500">-{prod.percentuale || 5}%</span>
                      </div>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Entrate</p>
                          <p className="font-mono font-bold text-emerald-600">+{formatNumber(prod.entrate)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Uscite</p>
                          <p className="font-mono font-bold text-blue-600">-{formatNumber(prod.uscite)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Scarto</p>
                          <p className="font-mono font-bold text-amber-600">-{formatNumber(prod.scarto)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Saldo</p>
                          <p className={`font-mono font-bold ${saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {saldo >= 0 ? '+' : ''}{formatNumber(saldo)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nessun movimento questa settimana</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base lg:text-lg font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            Ultimi Movimenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.ultimi_movimenti?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stats.ultimi_movimenti.slice(0, 6).map((mov, index) => (
                <div
                  key={mov.id || index}
                  data-testid={`activity-item-${index}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-md ${
                    mov.tipo === "entrata" 
                      ? "bg-emerald-50/50 border-emerald-200" 
                      : (mov.cliente || "").toUpperCase() === "SCARTO"
                        ? "bg-amber-50/50 border-amber-200"
                        : "bg-blue-50/50 border-blue-200"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    mov.tipo === "entrata" 
                      ? "bg-emerald-100 text-emerald-600" 
                      : (mov.cliente || "").toUpperCase() === "SCARTO"
                        ? "bg-amber-100 text-amber-600"
                        : "bg-blue-100 text-blue-600"
                  }`}>
                    {mov.tipo === "entrata" ? <ArrowDownToLine size={16} /> : <ArrowUpFromLine size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm font-bold text-slate-900">{mov.codice_partita}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {mov.tipo === "entrata" ? mov.fornitore : mov.cliente}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-bold ${mov.tipo === "entrata" ? "text-emerald-600" : "text-blue-600"}`}>
                      {mov.tipo === "entrata" ? "+" : "-"}{formatNumber(mov.quantita)}
                    </p>
                    <p className="text-[10px] text-slate-400">kg</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nessun movimento registrato</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
